import React, { useEffect, useState } from 'react'
import { FunnelSimple } from '@phosphor-icons/react'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
import { UsersTimesOff, UsersTribeOrg, TimeOffEntryDetail } from '../../types/TimeOff'

import { ReportTableHeaders } from './ReportTableHeaders'
import { ReportTableTimeOffContent } from './ReportTableTimeOffContent'
import { ReportTableTribeOrgContent } from './ReportTableTribeOrgContent'
import '../../css/reportTable.scss'
import { Guild } from '../../types/Guilds'
import moment from 'moment'
import { getStatus } from '../../utils/general'
import { timeOffOverlapsReportRange } from '../../hooks/useTimeoffCalculator'
import ReportTableTitleBar from './ReportTableTitleBar'
import Loader from '../Loader/Loader'

interface Props {
  usersInfo: UsersTimesOff[] | UsersTribeOrg[]
  calculateNextTimeOff: (
    timeoff: Array<any>,
    reportRange?: { rangeStart: Date; rangeEnd: Date },
  ) => TimeOffEntryDetail[]
  selectedReport: string
  selectedTeam?: string
  tableHeaders: Array<string>
  guilds: Array<Guild>
  loading?: boolean
}

const ReportTable = ({
  usersInfo,
  calculateNextTimeOff,
  selectedReport,
  selectedTeam,
  tableHeaders,
  guilds,
  loading,
}: Props) => {
  const [timeoffExportData, setTimeoffExportData] = useState<Array<object>>([
    {},
  ])
  const [tribeOrgExportData, setTribeOrgExportData] = useState<Array<object>>([
    {},
  ])

  const startDate = useSelector((state: RootState) => state.date.startDate)
  const endDate = useSelector((state: RootState) => state.date.endDate)

  interface Column {
    value: string
    widthPx: number
  }

  const getTimeoffExportData = () => {
    const columns: Column[] = [
      { value: 'Member', widthPx: 220 },
      { value: 'Guild', widthPx: 160 },
      { value: 'Tribe(s)', widthPx: 180 },
      { value: 'Team(s)', widthPx: 180 },
      { value: 'Start Date', widthPx: 110 },
      { value: 'End Date', widthPx: 110 },
      { value: 'Days', widthPx: 70 },
      { value: 'Status', widthPx: 90 },
      { value: 'Timeframe', widthPx: 110 },
    ]

    const timeoffUserInfo = usersInfo as UsersTimesOff[]

    const sortedMembers = [...timeoffUserInfo].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
      return nameA.localeCompare(nameB)
    })

    const entryRows: Array<Array<string | number>> = []
    const zeroTimeOffRows: Array<Array<string | number>> = []

    sortedMembers.forEach(employee => {
      const memberName = `${employee.firstName} ${employee.lastName}`
      const guildName =
        guilds.find(guild => guild.id === employee.guildId)?.role ?? ''
      const tribes = (employee.tribes ?? []).map(tribe => tribe.name).join(', ')
      const teams = (employee.teamAllocations ?? [])
        .map(allocation => allocation.team?.name || '')
        .filter(Boolean)
        .join(', ')

      const timeoffsInRange =
        startDate && endDate
          ? (employee.timeoffs ?? []).filter(timeoff =>
              timeOffOverlapsReportRange(timeoff, startDate, endDate),
            )
          : employee.timeoffs ?? []

      const entries =
        startDate && endDate
          ? calculateNextTimeOff(timeoffsInRange, { rangeStart: startDate, rangeEnd: endDate })
          : calculateNextTimeOff(timeoffsInRange)

      const sortedEntries = [...entries].sort(
        (a, b) => a.startDate.valueOf() - b.startDate.valueOf(),
      )

      if (sortedEntries.length === 0) {
        zeroTimeOffRows.push([memberName, guildName, tribes, teams, '', '', '', '', ''])
        return
      }

      sortedEntries.forEach(entry => {
        entryRows.push([
          memberName,
          guildName,
          tribes,
          teams,
          entry.startDate.format('DD-MMM-YYYY'),
          entry.endDate.format('DD-MMM-YYYY'),
          entry.days,
          entry.status,
          entry.timeframe,
        ])
      })
    })

    const dataToExport = [
      {
        columns,
        data: [...entryRows, ...zeroTimeOffRows],
      },
    ]
    return dataToExport
  }

  const getTribeOrgExportData = () => {
    const columns: Column[] = [
      { value: 'MEMBER', widthPx: 300 },
      { value: 'STATUS', widthPx: 100 },
      { value: 'GUILD', widthPx: 100 },
      { value: 'TRIBE', widthPx: 100 },
      { value: 'TEAM(ALLOCATION)', widthPx: 200 },
      { value: 'START DATE', widthPx: 100 },
    ]

    const membersMapped = usersInfo.map(employee => {
      const tribes = employee.tribes.map(tribe => `${tribe?.name}`)
      const teams = employee.teamAllocations.map(
        allocation => `${allocation.team.name} (${allocation.allocation}%)`,
      )
      if (selectedReport === 'Tribe Org') {
        return [
          `${employee.firstName} ${employee.lastName}`,
          `${guilds.find(guild => guild.id === employee.guildId)?.role ?? ''}`,
          getStatus(employee),
          tribes.join(', '),
          teams.join(', '),
          moment(employee.start_on).format('MM/DD/YYYY')
        ]
      }
    })
    const dataToExport = [
      {
        columns,
        data: membersMapped,
      },
    ]
    return dataToExport
  }

  useEffect(() => {
    selectedReport === 'Tribe Org' &&
      setTribeOrgExportData(getTribeOrgExportData())
    selectedReport === 'Time Off' &&
      setTimeoffExportData(getTimeoffExportData())
  }, [usersInfo, selectedReport, guilds, startDate, endDate])

  const columnCount = tableHeaders.length + 1

  return (
    <>
      <ReportTableTitleBar
        selectedReport={selectedReport}
        exportData={
          selectedReport === 'Time Off' ? timeoffExportData : tribeOrgExportData
        }
        startDate={startDate}
        endDate={endDate}
      />
      <div className="reports-table-wrap">
        <table className="reports-table">
          <ReportTableHeaders
            tableHeaders={tableHeaders}
            userCount={usersInfo.length}
          />
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={columnCount}>
                  <div className="reports-loading">
                    <Loader fitContainer />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : usersInfo.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columnCount}>
                  <div className="reports-empty-state">
                    <FunnelSimple size={48} weight="regular" />
                    <p>No results found. Try adjusting your filters.</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <>
              {selectedReport === 'Time Off' && (
                <ReportTableTimeOffContent
                  usersInfo={usersInfo}
                  calculateNextTimeOff={calculateNextTimeOff}
                />
              )}
              {selectedReport === 'Tribe Org' && (
                <ReportTableTribeOrgContent usersInfo={usersInfo} />
              )}
            </>
          )}
        </table>
      </div>
    </>
  )
}

export default ReportTable
